/**
 * Aktualisiert den Status einer Anfrage auf S3 und gibt das aktualisierte Inquiry-Objekt zurück.
 */
export const updateInquiryStatusOnS3 = async (id: string, status: InquiryStatus): Promise<Inquiry | null> => {
  const key = `inquiries/data/${id}.json`;
  try {
    const res = await s3.send(
      new GetObjectCommand({
        Bucket: S3_BUCKET,
        Key: key,
      })
    );
    const text = await res.Body?.transformToString();
    if (!text) return null;
    const inquiry: Inquiry = JSON.parse(text);
    inquiry.status = status;
    await s3.send(
      new PutObjectCommand({
        Bucket: S3_BUCKET,
        Key: key,
        Body: JSON.stringify(inquiry),
        ContentType: 'application/json',
      })
    );
    return inquiry;
  } catch (err) {
    return null;
  }
};
import { s3 } from './awsS3Storage';
import { PutObjectCommand, ListObjectsV2Command, GetObjectCommand } from '@aws-sdk/client-s3';

const AWS_REGION = import.meta.env.VITE_AWS_REGION;
const S3_BUCKET = import.meta.env.VITE_AWS_S3_BUCKET;

/**
 * Deletes an inquiry by id and returns the updated inquiries array.
 */
export const deleteInquiry = (id: string): Inquiry[] => {
  const inquiries = getInquiries().filter((inq) => inq.id !== id);
  saveInquiries(inquiries);
  return inquiries;
};
/**
 * Updates the status of an inquiry and returns the updated inquiries array.
 */
export const updateInquiryStatus = (id: string, status: InquiryStatus): Inquiry[] => {
  const inquiries = getInquiries().map((inq) =>
    inq.id === id ? { ...inq, status } : inq
  );
  saveInquiries(inquiries);
  return inquiries;
};
/**
 * Adds a new inquiry and returns the created inquiry object.
 */
export const createInquiry = (type: InquiryType, data: Record<string, any>): Inquiry => {
  const inquiries = getInquiries();
  const newInquiry: Inquiry = {
    id: crypto.randomUUID(),
    type,
    status: "new",
    createdAt: Date.now(),
    data,
  };
  inquiries.push(newInquiry);
  saveInquiries(inquiries);
  return newInquiry;
};
export type InquiryType = "nurse" | "employer" | "contact";

export interface Inquiry {
  id: string;
  type: InquiryType;
  status: InquiryStatus;
  createdAt: number;
  data: Record<string, any>;
}

export type InquiryStatus = "new" | "in_progress" | "done";

const STORAGE_KEY = "inquiries";

export const getInquiries = (): Inquiry[] => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : [];
  } catch {
    return [];
  }
};

const saveInquiries = (list: Inquiry[]) => {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(list));
};

export const addInquiry = (type: InquiryType, data: Record<string, any>) => {
  const inquiries = getInquiries();
  const newInquiry: Inquiry = {
    id: crypto.randomUUID(),
    type,
    status: "new",
    createdAt: Date.now(),
    data,
  };
  inquiries.push(newInquiry);
  saveInquiries(inquiries);
};

export const saveInquiryToS3 = async (inquiry: Inquiry) => {
  const key = `inquiries/data/${inquiry.id}.json`;
  const body = JSON.stringify(inquiry);
  await s3.send(
    new PutObjectCommand({
      Bucket: S3_BUCKET,
      Key: key,
      Body: body,
      ContentType: 'application/json',
    })
  );
};

export const loadAllInquiriesFromS3 = async (): Promise<Inquiry[]> => {
  const response = await s3.send(
    new ListObjectsV2Command({
      Bucket: S3_BUCKET,
      Prefix: 'inquiries/data/',
    })
  );
  const files = response.Contents?.filter(obj => obj.Key && obj.Key.endsWith('.json')) || [];
  const inquiries: Inquiry[] = [];
  for (const file of files) {
    const res = await s3.send(
      new GetObjectCommand({
        Bucket: S3_BUCKET,
        Key: file.Key!,
      })
    );
    const text = await res.Body?.transformToString();
    if (text) {
      const inquiry = JSON.parse(text);
      // Status explizit prüfen und auf gültige Werte setzen
      if (!["new", "in_progress", "done"].includes(inquiry.status)) {
        inquiry.status = "new";
      }
      inquiries.push(inquiry);
    }
  }
  return inquiries;
};

